import {gql, QueryResult} from '@apollo/client';
import {partition} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import {useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {assertIsAccessible, getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import omitKey from 'util/omitKey';

import {
  CollaborationManagementStateQuery,
  CollaborationManagementStateQueryVariables,
} from './__generated/CollaborationManagementStateQuery';

type GqlPoliceable = ExcludeInaccessible<CollaborationManagementStateQuery['policeable']>;

type GqlCollaboration = GqlPoliceable['inheritedCollaborations'][number];

export const COLLABORATION_MANAGEMENT_STATE_QUERY: DocumentNode = gql`
  # policeable.inheritedCollaborators does not query id
  # eslint-disable benchlint/id-field-required
  query CollaborationManagementStateQuery($id: StrictString!) @skipAudit {
    policeable(id: $id) {
      ...InaccessibleInfoGraphQL
      ... on PoliceableInterface {
        id
        name
        inheritedCollaborations {
          party {
            ... on PartyInterface {
              id
              name
              avatarUrl
              handle
            }
            ... on GroupInterface {
              admin
            }
          }
          policy {
            ... on PolicyInterface {
              id
              name
              isHidden
            }
          }
          organizationRole
          teamRole
          auditor
          fromOwnership
          resource {
            ...InaccessibleInfoGraphQL
            ... on RestrictedResourceNode {
              id
              name
              type
            }
          }
        }
        permissions {
          canManageCollaborators
          admin
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type Policeable = Omit<GqlPoliceable, 'inheritedCollaborations'>;

export type Collaboration = GqlCollaboration;

export type CollaborationManagementState =
  | SuccessfulCollaborationManagementState
  | NonSuccessfulCollaborationManagementState;

export interface SuccessfulCollaborationManagementState {
  queryResultType: QueryResultType.SUCCESS;
  policeable?: Policeable;
  // The set of collaborations on the `policeable`.
  initialCollaborations: Array<Collaboration>;
  // The set of collaborations from ancestors of the `policeable` (not including itself).
  ancestorCollaborations: Array<Collaboration>;
  // The set of collaborations on the `policeable` and any that have been added during the current session but
  // not yet saved.
  collaborations: Array<Collaboration>;
}

interface NonSuccessfulCollaborationManagementState {
  queryResultType: Exclude<QueryResultType, QueryResultType.SUCCESS>;
  initialCollaborations: Array<Collaboration>;
  collaborations: Array<Collaboration>;
}

export interface CollaborationManagementActions {
  setCollaborations: (collaborations: Array<Collaboration>) => void;
  refetch?: QueryResult['refetch'];
}

function isTenantResource(collaboration: Collaboration): boolean {
  return (
    collaboration.resource != null &&
    collaboration.resource.__typename === 'RestrictedResourceNode' &&
    collaboration.resource.type === 'tenant'
  );
}

function partitionCollaborations(policeable: GqlPoliceable): [Array<Collaboration>, Array<Collaboration>] {
  const nonTenantCollaborations = policeable.inheritedCollaborations.filter((c) => !isTenantResource(c));
  return partition(
    nonTenantCollaborations,
    (c: Collaboration) => c.resource != null && getAccessibleOrInaccessibleId(c.resource) === policeable.id
  );
}

/**
 * A hook to fetch and setup the required state and actions to use all the variants of the
 * CollaborationManagement UI.
 */
export default function useCollaborationManagementState(
  policeableId?: string
): [CollaborationManagementState, CollaborationManagementActions] {
  const result = useSkippableBenchlingQuery<
    CollaborationManagementStateQuery,
    CollaborationManagementStateQueryVariables
  >(COLLABORATION_MANAGEMENT_STATE_QUERY, {
    variables: {id: policeableId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: policeableId == null,
  });
  // This is undefined when the query is still loading but is never passed to downstream components.
  const [collaborations, setCollaborations] = useState<Array<Collaboration> | undefined>(undefined);

  if (result.type === QueryResultType.SKIPPED) {
    return [
      {
        queryResultType: QueryResultType.SUCCESS,
        initialCollaborations: [],
        ancestorCollaborations: [],
        collaborations: collaborations ?? [],
      },
      {setCollaborations},
    ];
  }
  if (result.type !== QueryResultType.SUCCESS) {
    return [
      {queryResultType: result.type, initialCollaborations: [], collaborations: []},
      {setCollaborations: (collaborations: Array<Collaboration>) => {}},
    ];
  }
  const policeable = assertIsAccessible(result.data.policeable);
  const [initialCollaborations, ancestorCollaborations] = partitionCollaborations(policeable);
  return [
    {
      queryResultType: result.type,
      policeable: omitKey(policeable, 'inheritedCollaborations'),
      initialCollaborations,
      ancestorCollaborations,
      collaborations: collaborations ?? initialCollaborations,
    },
    {setCollaborations, refetch: result.refetch},
  ];
}
